import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast({ title: "Password updated" });
      navigate("/login");
    } catch (err) {
      toast({
        title: "Reset failed",
        description: err?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="reset-password-page">
      <div className="mx-auto grid min-h-screen max-w-6xl grid-cols-1 gap-8 px-5 py-10 lg:grid-cols-2">
        <div className="flex flex-col justify-center">
          <h1
            className="text-4xl font-semibold tracking-tight text-slate-100 sm:text-5xl lg:text-6xl"
            data-testid="reset-password-title"
          >
            Set a new password
          </h1>
          <p className="mt-4 max-w-xl text-base text-slate-200/75" data-testid="reset-password-subtitle">
            Choose a strong password. This will immediately replace your old one.
          </p>
          <div className="mt-6" data-testid="reset-password-links">
            <Link
              to="/login"
              className="text-sm text-slate-200/80 underline-offset-4 hover:underline"
              data-testid="reset-password-back-to-login-link"
            >
              Back to login
            </Link>
          </div>
        </div>

        <div className="flex items-center justify-center lg:justify-end">
          <Card className="w-full max-w-md glass" data-testid="reset-password-card">
            <CardHeader>
              <CardTitle className="text-slate-100" data-testid="reset-password-card-title">
                Reset password
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!token ? (
                <div className="text-sm text-slate-200/70" data-testid="reset-password-missing-token-text">
                  Missing token. Please use the link from your email.
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form">
                  <div className="space-y-2">
                    <label className="text-sm text-slate-200/80" data-testid="reset-password-new-password-label">
                      New password
                    </label>
                    <Input
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      type="password"
                      autoComplete="new-password"
                      className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                      data-testid="reset-password-new-password-input"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
                    data-testid="reset-password-submit-button"
                  >
                    {loading ? "Updating…" : "Update password"}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
